import React, {useEffect, useMemo} from 'react'
import {ButtonWithModal, Modal} from '@therms/atalaya'
import {
  Alerts,
  DateTime,
  RPCDocs,
  RPCs,
  useCurrentUser,
  useGeolocation,
  useRPC,
  useRPCContext,
  useRPCServerEventHandler,
} from '@therms/react-modules'
import {ActivateEmergencySOS} from '@src/modules/Users/shared/Layout/CurrentUserStatusInteractiveCard/components/EmergencySOS/components/ActivateEmergencySOS'
import SOS from '@src/assets/sos.svg'
import classnames from 'classnames'
import {getDevice} from '@therms/web-js'

function EmergencySOS() {
  const currentUser = useCurrentUser()

  const {rpcService} = useRPCContext()

  const {accuracy, lat, lng, refresh, timestamp} = useGeolocation()

  const {call, data} = useRPC<
    RPCDocs.UserSos.GetActivatedUserSos.v1.Args,
    RPCDocs.UserSos.GetActivatedUserSos.v1.Data
  >(RPCs.UserSos.GetActivatedUserSos.v1)

  useRPCServerEventHandler('UserSOSActivated', call)
  useRPCServerEventHandler('UserSOSActivationUpdated', call)

  const currentUserActivatedSOS = useMemo<
    RPCDocs.UserSos.GetActivatedUserSos.v1.UserSOSs | undefined
  >(() => data?.userSOSs.find(({userId}) => userId === currentUser.id), [data])

  const deviceGeo = useMemo(
    () =>
      lat && lng
        ? {
            accuracy,
            coords: {
              lat,
              lng,
            },
            device: getDevice(),
            timestamp,
          }
        : undefined,
    [accuracy, lat, lng, timestamp],
  )

  useEffect(() => {
    if (currentUserActivatedSOS && deviceGeo) {
      rpcService.UserSos.UpdateActivatedUserSos.v1.call({deviceGeo})
    }
  }, [deviceGeo])

  const rpcCallActivateSOS = () => {
    rpcService.UserSos.ActivateUserSos.v1
      .call({
        deviceGeo,
      })
      .catch((err: any) => {
        Alerts.error('Failed to activate SOS, retrying', err.message)

        setTimeout(() => {
          if (!currentUserActivatedSOS) return

          rpcCallActivateSOS()
        }, 3000)
      })
  }

  const handleActivate = () => {
    rpcCallActivateSOS()
  }

  const handleDeactivate = () => {
    rpcService.UserSos.DeactivateUserSos.v1.call({})
  }

  return (
    <div className="border-critical-faded flex flex-col justify-center rounded border border-dotted text-sm">
      <ButtonWithModal
        className={classnames({
          'h-12': !currentUserActivatedSOS,
        })}
        modalElement={({closeHandler}) => (
          <Modal
            closeHandler={closeHandler}
            escPressClose
            header="Emergency SOS"
          >
            <ActivateEmergencySOS
              activatedTimestamp={currentUserActivatedSOS?.activatedTimestamp}
              hasGPSTracking={!!deviceGeo}
              gpsAccuracy={deviceGeo?.accuracy || 0}
              isActivated={!!currentUserActivatedSOS}
              onActivate={handleActivate}
              onCancel={() => {
                if (currentUserActivatedSOS) {
                  handleDeactivate()
                }

                closeHandler()
              }}
              userSOSId={currentUserActivatedSOS?.id}
            />
          </Modal>
        )}
        onClick={() => {
          refresh()
        }}
        subtle={!currentUserActivatedSOS}
        variant="critical"
      >
        <div className={currentUserActivatedSOS ? 'animate-pulse' : ''}>
          {currentUserActivatedSOS && (
            <span className="flex w-full justify-center">
              <img alt="" style={{height: 50, width: 50}} src={SOS} />
            </span>
          )}

          <div className="flex flex-col">
            <div
              className={
                currentUserActivatedSOS ? 'text-lg' : 'text-color-critical'
              }
            >
              EMERGENCY S.O.S.
            </div>
            {currentUserActivatedSOS && (
              <div className="text-color-white text-lg">
                Activated{' '}
                <DateTime
                  mode="time"
                  relative
                  timestamp={currentUserActivatedSOS?.activatedTimestamp}
                />
              </div>
            )}
          </div>
        </div>
      </ButtonWithModal>
    </div>
  )
}

export {EmergencySOS}
